import * as React from "react";
import { Modal, Typography, Card, Grid, Button, IconButton, Icon } from "@mui/material";
import { makeStyles } from "@mui/styles";
import NumberFormat from "react-number-format";
import CancelIcon from "../../../icons/cancelIcon.svg";
import { useDispatch, useSelector } from "react-redux";
import { getAdhocRefund, getApproveRefund, selectPMPaymentState } from "../sagas/pmTransactionsSlice";
import SpinnerModal from "../../../shared/Modal/SpinnerModal";
import TransactionDetails from "./TransactionDetails";

const useStyles = makeStyles({
  modalContainer: {
    background: "#FFFFFF",
    boxShadow: "none",
    borderRadius: "24px",
  },
});

interface approveModalProps {
  open: boolean;
  closeCallback: (event: object, reason: string) => void;
  closeBaseModal: (event: object, reason: string) => void;
  amount: number;
  residentName: string;
  transactionUuid: string;
  refundStatus: string | undefined | null;
  globalError: string | null;
  setGlobalError: React.Dispatch<React.SetStateAction<string | null>>;
}

export default function ApproveModal(props: approveModalProps) {
  const classes = useStyles();
  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    padding: "16px",
    height: "555px",
    width: "520px",
  };
  const [isApproveModalConfirmationOpen, setIsApproveModalConfirmationOpen] = React.useState(false);
  const dispatch = useDispatch();

  const handleCloseApproveModalConfirmation = (event: object, reason: string) => {
    setIsApproveModalConfirmationOpen(false);
  };

  const handleClickApprove = () => {
    if ([null, undefined, "Denied"].includes(props.refundStatus)) {//if it's not requested or previously denied, we'll allow adhoc
      dispatch(getAdhocRefund({ transactionUuid: props.transactionUuid }));
    } else {
      dispatch(getApproveRefund({ transactionUuid: props.transactionUuid }));
    }
    setIsApproveRefundSpinnerModalOpen(true);
  };

  const handleCloseApproveRefundSpinnerModal = React.useCallback(() => {
    setIsApproveRefundSpinnerModalOpen(false); //close spinner
    setIsApproveModalConfirmationOpen(true); // open confirmation page
  }, []);

  const { isApproveRefundSuccess, isApproveRefundError, approveRefundError, isAdhocRefundSuccess, isAdhocRefundError, adhocRefundError } = useSelector(selectPMPaymentState);

  const [isApproveRefundSpinnerModalOpen, setIsApproveRefundSpinnerModalOpen] = React.useState(false);
  const [approveRefundSpinnerStatus, setApproveRefundSpinnerStatus] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isApproveRefundSuccess || isAdhocRefundSuccess) {
      props.setGlobalError(null);
      setApproveRefundSpinnerStatus("stop");
      props.closeBaseModal({}, "close Trx Details Modal");
    }
  }, [isApproveRefundSuccess, isAdhocRefundSuccess]);

  React.useEffect(() => {
    if (isApproveRefundError || isAdhocRefundError) {
      props.closeCallback({}, "Refund processing failed");
      props.setGlobalError(approveRefundError?.length ? approveRefundError : adhocRefundError);
    }
  }, [isApproveRefundError, approveRefundError, props.setGlobalError, handleCloseApproveRefundSpinnerModal, isAdhocRefundError, adhocRefundError]);

  return (
    <div className={classes.modalContainer}>
      <Modal open={props.open} onClose={() => props.closeCallback({}, "closeDetailsModal")} aria-labelledby="simple-modal-title" aria-describedby="simple-modal-description">
        <Card sx={style}>
          <>
            <Grid container>
              <Grid item xs={2}></Grid>
              <Grid item xs={8}>
                <Typography id="payment-id" sx={{ fontWeight: "600", mt: 2 }}>
                  Refund Payment
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <IconButton sx={{ ml: "30px" }} onClick={() => props.closeCallback({}, "closeDetailsModal")}>
                  <Icon>
                    <img src={CancelIcon} alt="" width={24} height={24} />
                  </Icon>
                </IconButton>
              </Grid>
            </Grid>
            <Typography id="payment-id" sx={{ mt: 2 }}>
              You are issuing refund to {props.residentName} for &#8202;
              <NumberFormat thousandsGroupStyle="thousand" value={props.amount} prefix="$" decimalSeparator="." displayType="text" type="text" thousandSeparator={true} allowNegative={true} decimalScale={2} fixedDecimalScale={true} />
              &#8202; It will be returned to the original payment method. This cannot be cancelled.
            </Typography>
            <Typography id="payment-id" sx={{ mt: 2 }}>
              Are you sure you want to proceed?
            </Typography>
            <Grid container sx={{ marginTop: "34px", width: "100%", display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                style={{
                  fontFamily: "'Open Sans', sans-serif",
                  fontStyle: "normal",
                  fontWeight: 700,
                  width: "87px",
                  background: "#A2DBFA",
                  borderRadius: "24px",
                }}
                onClick={handleClickApprove}
              >
                Yes
              </Button>
              <Button
                variant="contained"
                style={{
                  fontFamily: "'Open Sans', sans-serif",
                  fontStyle: "normal",
                  fontWeight: 700,
                  width: "87px",
                  marginLeft: "8px",
                  background: "#39A2DB",
                  borderRadius: "24px",
                }}
                onClick={() => props.closeCallback({}, "closeDetailsModal")}
              >
                No
              </Button>
            </Grid>
          </>
          {/* approve refund spinner */}
          {isApproveRefundSpinnerModalOpen ? (
            <SpinnerModal
              openSpinnerModal={isApproveRefundSpinnerModalOpen}
              handleCloseSpinnerModal={handleCloseApproveRefundSpinnerModal}
              spinnerStatus={approveRefundSpinnerStatus}
              closeCancel={() => props.closeCallback({}, "closeThankYouModal")}
              width="520px"
              height="555px"
            />
          ) : (
            <></>
          )}
        </Card>
      </Modal>
      {/* confirmation page */}
      {isApproveModalConfirmationOpen ? <TransactionDetails open={isApproveModalConfirmationOpen} closeCallback={handleCloseApproveModalConfirmation} refundReceipt={true} /> : <></>}
    </div>
  );
}
